/*
 * Lightweight auth event bus. The axios interceptor fires `cleared` when a
 * refresh attempt fails; SessionProvider listens and drops its in-memory session
 * so React Router's ProtectedGate redirects to /login on the next render.
 *
 * Using an event bus instead of `window.location.assign('/login')` avoids full
 * page reloads mid-bootstrap and keeps the SW / TanStack Query caches intact.
 */

/**
 * `cleared`: the sign-in is gone (refresh failed).
 * `outletCleared`: the API refused the request's outlet (400 "choose an outlet"
 * or 403 "not assigned to that outlet"); the interceptor dropped it and
 * OutletGate asks again.
 * `sectionCleared`: the stored section is no longer on the floor (the board
 * found none of its tables); SectionGate asks again.
 */
type AuthEvent = 'cleared' | 'outletCleared' | 'sectionCleared';
type Listener = () => void;

const listeners: Record<AuthEvent, Set<Listener>> = {
  cleared: new Set(),
  outletCleared: new Set(),
  sectionCleared: new Set(),
};

export const authEvents = {
  on(event: AuthEvent, fn: Listener): () => void {
    listeners[event].add(fn);
    return () => {
      listeners[event].delete(fn);
    };
  },
  emit(event: AuthEvent): void {
    listeners[event].forEach((fn) => fn());
  },
};
